import { Shield, Globe, MapPin, Grid, AlertTriangle } from 'lucide-react'
import { type ReactElement, useEffect, useRef } from 'react'
import {
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
  type FieldErrors,
} from 'react-hook-form'

import { type RealmRolesFormData, type UserFormInput } from '@/lib/validation/user-schemas'

interface RolePermissionsSectionProps {
  register: UseFormRegister<UserFormInput>
  setValue: UseFormSetValue<UserFormInput>
  watch: UseFormWatch<UserFormInput>
  errors: FieldErrors<UserFormInput>
}

interface RoleItem {
  key: keyof RealmRolesFormData
  label: string
  description: string
  icon: ReactElement
  category: 'access' | 'status' | 'scope'
}

const roleGroups: Record<string, RoleItem[]> = {
  'Access Levels': [
    {
      key: 'cs_sys_op',
      label: 'Root Operator',
      description:
        'Lowest level access with unrestricted administrative control over all systems, functions, and resources. Required for assigning Keycloak Client Roles.',
      icon: <Shield className="w-4 h-4" />,
      category: 'access',
    },
    {
      key: 'cs_sys_admin',
      label: 'System Administrator',
      description: 'Full administrative access within assigned systems',
      icon: <Shield className="w-4 h-4" />,
      category: 'access',
    },
  ],
  'Account Status Roles': [
    {
      key: 'cs_active',
      label: 'Active Account',
      description: 'Account is active and can be used',
      icon: <Shield className="w-4 h-4" />,
      category: 'status',
    },
    {
      key: 'cs_locked',
      label: 'Account Locked',
      description: 'Account is locked due to security concerns',
      icon: <Shield className="w-4 h-4" />,
      category: 'status',
    },
  ],
  'Resource Scope': [
    {
      key: 'cs_systems_all',
      label: 'All Systems',
      description: 'Access to all systems without restriction',
      icon: <Globe className="w-4 h-4" />,
      category: 'scope',
    },
    {
      key: 'cs_locations_all',
      label: 'All Locations',
      description: 'Access to all locations (also covers all DBAs and districts)',
      icon: <MapPin className="w-4 h-4" />,
      category: 'scope',
    },
    {
      key: 'cs_sections_all',
      label: 'All Sections',
      description: 'Access to all portal sections',
      icon: <Grid className="w-4 h-4" />,
      category: 'scope',
    },
  ],
}

function isRoleDisabled(role: RoleItem, isRootOperator: boolean, isSysAdmin: boolean): boolean {
  if (role.key === 'cs_sys_admin' && isRootOperator) return true
  if (role.key === 'cs_sys_op' && isSysAdmin) return true
  if (role.category !== 'scope') return false
  if (isRootOperator) return true
  return isSysAdmin && role.key !== 'cs_systems_all'
}

function useMutualExclusion(
  isRootOperator: boolean,
  isSysAdmin: boolean,
  setValue: UseFormSetValue<UserFormInput>
): void {
  useEffect(() => {
    if (isRootOperator && isSysAdmin) {
      setValue('realmRoles.cs_sys_admin', false)
    }
  }, [isRootOperator, isSysAdmin, setValue])

  useEffect(() => {
    if (isSysAdmin && isRootOperator) {
      setValue('realmRoles.cs_sys_op', false)
    }
  }, [isSysAdmin, isRootOperator, setValue])
}

function useClearScopeOnRoleTransition(
  isRootOperator: boolean,
  isSysAdmin: boolean,
  setValue: UseFormSetValue<UserFormInput>
): void {
  const prevRoles = useRef({ cs_sys_op: false, cs_sys_admin: false })

  useEffect(() => {
    const wasRootOperator = prevRoles.current.cs_sys_op
    const wasSysAdmin = prevRoles.current.cs_sys_admin

    if (isRootOperator && !wasRootOperator) {
      setValue('realmRoles.cs_systems_all', false)
      setValue('realmRoles.cs_locations_all', false)
      setValue('realmRoles.cs_sections_all', false)
    }

    if (isSysAdmin && !wasSysAdmin) {
      setValue('realmRoles.cs_locations_all', false)
      setValue('realmRoles.cs_sections_all', false)
    }

    prevRoles.current = { cs_sys_op: isRootOperator, cs_sys_admin: isSysAdmin }
  }, [isRootOperator, isSysAdmin, setValue])
}

const RoleCheckbox = ({
  role,
  register,
  disabled,
}: {
  role: RoleItem
  register: UseFormRegister<UserFormInput>
  disabled: boolean
}) => (
  <div key={role.key} className="flex items-start">
    <div className="flex items-center h-6">
      <input
        {...register(`realmRoles.${role.key}`)}
        type="checkbox"
        id={role.key}
        disabled={disabled}
        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
      />
    </div>
    <div className="ml-3">
      <label
        htmlFor={role.key}
        className={`text-sm font-medium flex items-center gap-2 ${
          disabled
            ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
            : 'text-gray-700 dark:text-gray-300'
        }`}
      >
        <span className="text-gray-500 dark:text-gray-400">{role.icon}</span>
        {role.label}
      </label>
      <p className="text-xs font-mono text-gray-400 dark:text-gray-500 mt-0.5">{role.key}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{role.description}</p>
    </div>
  </div>
)

const ResourceScopeInfoBanner = ({
  isRootOperator,
  isSysAdmin,
}: {
  isRootOperator: boolean
  isSysAdmin: boolean
}) => (
  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
    <div className="flex items-start gap-2">
      <AlertTriangle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
      <p className="text-xs text-blue-800 dark:text-blue-200">
        {isRootOperator && 'Root Operator has unrestricted access to all resources'}
        {isSysAdmin && 'System Administrator has access to all resources within assigned system(s)'}
      </p>
    </div>
  </div>
)

const RoleGuidelines = () => (
  <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
    <div className="flex items-start gap-3">
      <Shield className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
      <div className="text-sm text-amber-800 dark:text-amber-200">
        <p className="font-medium mb-1">Role assignment guidelines</p>
        <ul className="space-y-1 text-amber-700 dark:text-amber-300">
          <li>
            <strong>Root Operator:</strong> Use sparingly for trusted administrators only - grants
            full administrative access to all systems and resources
          </li>
          <li>
            <strong>System Administrator:</strong> Grants full access within assigned systems -
            mutually exclusive with Root Operator
          </li>
          <li>
            <strong>Resource Scope:</strong> Only available when neither Root Operator nor System
            Administrator is assigned
          </li>
        </ul>
      </div>
    </div>
  </div>
)

export function RolePermissionsSection({
  register,
  setValue,
  watch,
  errors: _errors,
}: RolePermissionsSectionProps) {
  const realmRoles = watch('realmRoles')
  const isRootOperator = realmRoles.cs_sys_op === true
  const isSysAdmin = realmRoles.cs_sys_admin === true
  const hasHighLevelRole = isRootOperator || isSysAdmin

  useMutualExclusion(isRootOperator, isSysAdmin, setValue)
  useClearScopeOnRoleTransition(isRootOperator, isSysAdmin, setValue)

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="p-6 space-y-6">
        <div className="pb-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Realm Roles & Permissions
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Assign realm-level roles that control access scope and permissions
          </p>
        </div>

        <div className="space-y-6">
          {Object.entries(roleGroups).map(([groupName, roles]) => (
            <div key={groupName}>
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                {groupName}
              </h4>
              <div
                className={`space-y-3 ${groupName === 'Resource Scope' && isRootOperator ? 'opacity-50' : ''}`}
              >
                {roles.map((role) => (
                  <RoleCheckbox
                    key={role.key}
                    role={role}
                    register={register}
                    disabled={isRoleDisabled(role, isRootOperator, isSysAdmin)}
                  />
                ))}
              </div>
              {groupName === 'Resource Scope' && hasHighLevelRole && (
                <ResourceScopeInfoBanner isRootOperator={isRootOperator} isSysAdmin={isSysAdmin} />
              )}
            </div>
          ))}
        </div>

        <RoleGuidelines />
      </div>
    </div>
  )
}
